// import components
import Box from '@mui/material/Box';
import EditPage from '../../../../../../../shared/CRUD/components/InnerPage/EditPage';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import CostCenterDetailsTable from './CostCenterDetailsTable';
import PlanUnitFormFields from './PlanUnitFormFields';

// import hooks
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import useForceRefresh from '../../../../../../../shared/hooks/useForceRefresh';
import useEditPlanUnit from '../../../hooks/useEditPlanUnit';
import useGetCostCenterDetailsById from '../../../hooks/useGetCostCenterDetailsById';

// import types
import {
  CostCenterDetail,
  PlanUnit,
  PlanUnitErrorResponse
} from '../../../types';
import { EditPlanUnitProps } from './EditPlanUnit.types';

// import utils
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { convertFlagsArrayToObjects } from 'utils/misc';
import { handleSaveCostCenterDetails } from '../../utils/handleSaveCostCenterDetails';
import {
  apiToFormAdapter,
  formatCostCenterDetailLabel,
  formToApiAdapterBase,
  mapFormFlagsForApi
} from '../utils/utils';
import { editPlanUnitSchema } from '../planUnitSchema';
import { t } from 'i18next';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';

const EditPlanUnit: React.FC<EditPlanUnitProps> = ({
  onSaveChanges,
  rowData,
  rows,
  toggleInnerLayout
}) => {
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const handleOnCancel = () => {
    setDeletedIds([]);
    methods.reset();
  };

  const [currentViewData, setCurrentViewData] = useState<PlanUnit>(rowData);

  const { timestamp: isEditable, refresh: setIsEditable } = useForceRefresh();

  const methods = useForm({
    defaultValues: editPlanUnitSchema.cast(apiToFormAdapter(currentViewData)),
    resolver: yupResolver(editPlanUnitSchema),
    mode: 'onChange'
  });

  const { mutateAsync } = useEditPlanUnit();

  const { data: costCenterDetails } = useGetCostCenterDetailsById(
    currentViewData.brId,
    (response) => {
      return response.data.map((costCenter) => ({
        ...costCenter,
        flags: convertFlagsArrayToObjects(costCenter.flags)
      }));
    }
  );

  useEffect(() => {
    if (costCenterDetails) {
      methods.reset({
        ...editPlanUnitSchema.cast({
          ...apiToFormAdapter(currentViewData),
          costCenterDetails: costCenterDetails.map(
            ({ brDynId, finBrDynId, parentBrDynId, ...rest }) => ({
              brDynId,
              ...rest,
              valid: {
                from: rest.valid?.from ? dayjs(rest.valid.from) : null,
                to: rest.valid?.to ? dayjs(rest.valid.to) : null
              },
              finBrDynId: finBrDynId
                ? {
                    value: finBrDynId,
                    label: formatCostCenterDetailLabel(
                      rest as CostCenterDetail,
                      'financialBrName'
                    )
                  }
                : null,
              parentBrDynId: parentBrDynId
                ? {
                    value: parentBrDynId,
                    label: formatCostCenterDetailLabel(
                      { parentBrDynId, ...rest } as CostCenterDetail,
                      'parentBrName'
                    )
                  }
                : null
            })
          )
        })
      });
    }
  }, [costCenterDetails, currentViewData]);

  const onSubmit = async (data) => {
    try {
      await Promise.all([
        mutateAsync(formToApiAdapterBase(data)),
        handleSaveCostCenterDetails(
          {
            ...data,
            costCenterDetails: data.costCenterDetails.map(
              (costCenterDetail) => ({
                ...costCenterDetail,
                benefitStartDate: dayjs.isDayjs(
                  costCenterDetail.benefitStartDate
                )
                  ? costCenterDetail.benefitStartDate.toDate()
                  : costCenterDetail.benefitStartDate ?? null,
                flags: mapFormFlagsForApi(costCenterDetail.flags)
              })
            )
          },
          deletedIds
        )
      ]);
      showSuccessSnackbar();
      setIsEditable();
      setDeletedIds([]);
      methods.reset(data);
      onSaveChanges?.();
      await Promise.all([
        queryClient.invalidateQueries(['costCenterDetailsById']),
        queryClient.invalidateQueries(['hierarchy'])
      ]);
    } catch (error) {
      const err = error as AxiosError<PlanUnitErrorResponse>;
      if (
        err?.response?.data?.error?.message?.includes('db.error.duplicate_code')
      )
        return showErrorSnackbar(t('formErrors.duplicateCode'));
      showErrorSnackbar();
    }
  };

  return (
    <EditPage
      onCancel={handleOnCancel}
      toggleInnerLayout={toggleInnerLayout}
      currentView={currentViewData}
      setCurrentView={setCurrentViewData}
      views={rows}
      isEditable={isEditable}
      methods={methods}
      onSubmit={onSubmit}
      navigatorValueGetter={'brId'}
      navigatorLabelGetter={['name', 'code']}
    >
      <Box sx={{ width: '100%' }}>
        <PlanUnitFormFields />
      </Box>

      <CostCenterDetailsTable setDeletedIds={setDeletedIds} />
    </EditPage>
  );
};

export default EditPlanUnit;
